/**
 * E2E-only control for the Auto Link state observed by onboarding.
 *
 * The failure originates below React, so Playwright needs a deterministic way
 * to place the real gate in the same terminal state as an exhausted local run.
 * The probe writes the real depot slice and leaves every onboarding decision,
 * timer, and component untouched.
 *
 * Per rule: regression-tests-for-state-machine-primitives — use the real depot.
 */
import { depot } from '#~/depot/index.js';

type OnboardingAutoLinkFailure = 'attempts_exhausted' | 'network_unavailable';

interface OnboardingAutoLinkProbe {
  open(): void;
  begin(): void;
  fail(reason: OnboardingAutoLinkFailure): void;
}

declare global {
  var __irisOnboardingAutoLinkProbe: OnboardingAutoLinkProbe | undefined;
}

const isE2eEnabled = (): boolean =>
  globalThis.window?.electron?.process?.env?.IRIS_E2E_TEST === '1';

let openHandler: (() => void) | undefined;

/** The gate stays real; this only exposes its welcome transition to E2E. */
export const bindOnboardingAutoLinkOpen = (handler: () => void): (() => void) => {
  if (!isE2eEnabled()) return () => undefined;

  openHandler = handler;
  return () => {
    if (openHandler === handler) openHandler = undefined;
  };
};

if (isE2eEnabled()) {
  const probe: OnboardingAutoLinkProbe = {
    open(): void {
      if (openHandler === undefined) throw new Error('Onboarding Auto Link probe is not bound');
      openHandler();
    },
    begin(): void {
      depot.getState().produce((draft) => {
        draft.boot.autoLinkState = { status: 'checking', startedAt: Date.now() };
      });
    },
    fail(reason): void {
      depot.getState().produce((draft) => {
        draft.boot.autoLinkState = { status: 'skipped', reason };
      });
    }
  };

  globalThis.__irisOnboardingAutoLinkProbe = probe;
}
